import { memo, useCallback } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { UpcomingUnlocksBaseProps, UnlockItem } from './UpcomingUnlocksBase'
import { ProjectLogo } from './ProjectLogo'

const UnlockItemComponent = memo(({ 
  item, 
  onItemClick,
  onDetailsClick
}: { 
  item: UnlockItem
  onItemClick?: (item: UnlockItem) => void
  onDetailsClick?: (item: UnlockItem) => void
}) => {
  const handleClick = useCallback(() => {
    onItemClick?.(item)
  }, [item, onItemClick])

  const handleDetailsClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onDetailsClick?.(item)
  }, [item, onDetailsClick])

  return (
    <div
      className="bg-white dark:bg-[#1c1c1c] flex items-center justify-between pl-3 pr-[18px] py-2 cursor-pointer hover:opacity-90 transition-opacity gap-2"
      onClick={handleClick}
    >
      <div className="flex gap-[10px] items-start flex-1 min-w-0">
        <ProjectLogo logo={item.logo} color={item.color} alt={item.name} />
        <div className="flex gap-2 items-baseline leading-[0] min-w-0 flex-1">
          <p className="font-plus-jakarta font-medium leading-5 text-[#2d2923] dark:text-[#fdfdfd] text-sm overflow-hidden text-ellipsis whitespace-nowrap">
            {item.name}
          </p>
          <p className="font-plus-jakarta font-normal leading-4 text-xs text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] overflow-hidden text-ellipsis whitespace-nowrap">
            in {item.days} days
          </p>
          <p className="font-plus-jakarta font-medium leading-4 text-[#2d2923] dark:text-[#fdfdfd] text-xs overflow-hidden text-ellipsis whitespace-nowrap">
            (+{item.value}, {item.percentage}%)
          </p>
        </div>
      </div>
      <div className="flex gap-[8px] items-center flex-shrink-0">
        <button
          onClick={handleDetailsClick}
          className="bg-[rgba(45,41,35,0.1)] dark:bg-[rgba(253,253,253,0.1)] flex items-center justify-center px-[16px] py-[6px]"
        >
          <p className="font-plus-jakarta font-medium leading-4 text-[#2d2923] dark:text-[#fdfdfd] text-xs">
            Details
          </p>
        </button>
      </div>
    </div>
  )
})

UnlockItemComponent.displayName = 'UnlockItemComponent'

export const UpcomingUnlocksDesktop = ({ 
  items, 
  onItemClick,
  onDetailsClick,
  onMoreDetailsClick,
  loading = false
}: UpcomingUnlocksBaseProps) => {
  if (loading) {
    return (
      <div className="h-full flex flex-col relative">
        <p className="text-base leading-6 text-[#2d2923] dark:text-[#fdfdfd] font-medium uppercase">
          Upcoming Unlocks
        </p>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            Loading upcoming unlocks...
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-base leading-6 text-[#2d2923] dark:text-[#fdfdfd] font-medium uppercase">
          Upcoming Unlocks
        </p>
        <button
          onClick={onMoreDetailsClick}
          className="flex gap-1 items-center"
        >
          <p className="font-plus-jakarta font-medium leading-4 text-[#2d2923] dark:text-[#fdfdfd] text-xs underline decoration-solid [text-decoration-skip-ink:none] [text-underline-position:from-font]">
            More details
          </p>
          <ArrowUpRight className="w-3 h-3 text-[#2d2923] dark:text-[#fdfdfd]" />
        </button>
      </div>

      {/* Unlock items list */}
      <div className="flex-1 overflow-y-auto space-y-2 scrollbar-hide" style={{ maxHeight: 'calc(4 * (52px + 12px))' }}>
        {items.map((item) => (
          <UnlockItemComponent
            key={item.id}
            item={item}
            onItemClick={onItemClick}
            onDetailsClick={onDetailsClick}
          />
        ))}

        {items.length === 0 && (
          <div className="text-center py-8 text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            No upcoming unlocks
          </div>
        )}
      </div>
    </div>
  )
}

